import PropTypes from 'prop-types';
import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import AddNoteModal from 'components/AddNoteModal';
import Badge from 'components/Badge';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import CollapsibleText from 'components/CollapsibleText';
import DeleteConfirmationModal from 'pages/app/Accounts/AddAccounts/components/shared/DeleteConfirmationModal';
import { useGetAccount } from 'pages/app/Accounts/gql/queries/useGetAccount';

// Note type configuration with colors
const NOTE_TYPES = {
  event: { label: 'Event', color: '#8b5cf6' },
  issues: { label: 'Issues', color: '#ef4444' },
  business: { label: 'Business', color: '#3b82f6' },
  staff: { label: 'Staff', color: '#10b981' },
  hardware: { label: 'Hardware', color: '#f59e0b' },
  instructions: { label: 'Instructions', color: '#06b6d4' },
  payouts: { label: 'Payouts', color: '#84cc16' },
  other: { label: 'Other', color: '#6b7280' },
};

// Container
const Container = styled.div``;

// Section header with title and action button
const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 4px 0 0 0;
`;

const HeaderLeft = styled.div``;

const ItemCount = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
`;

// Wrapper for add button
const AddButtonWrapper = styled.div`
  button {
    width: auto;
    padding: 10px 20px;
  }
`;

// Filter section
const FilterSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 24px;
`;

const FilterChip = styled.button`
  padding: 6px 14px;
  border-radius: 20px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid
    ${({ active, theme }) =>
      active
        ? 'transparent'
        : theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : '#e2e8f0'};
  background: ${({ active, color, theme }) =>
    active
      ? color || '#fa4616'
      : theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : '#ffffff'};
  color: ${({ active, theme }) =>
    active ? '#ffffff' : theme?.name === 'dark' ? '#94a3b8' : '#64748b'};

  &:hover {
    border-color: ${({ color }) => color || '#fa4616'};
    color: ${({ active, color }) => (active ? '#ffffff' : color || '#fa4616')};
  }
`;

// Sub-section divider
const SubSection = styled.div`
  margin-bottom: 32px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};
`;

const SubSectionIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${({ color, theme }) =>
    color
      ? `${color}20`
      : theme?.name === 'dark'
      ? 'rgba(250, 70, 22, 0.15)'
      : 'rgba(250, 70, 22, 0.08)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ color }) => color || '#fa4616'};
`;

const SubSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`;

const SubSectionCount = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textGray.muted};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9'};
  padding: 4px 10px;
  border-radius: 12px;
`;

// Timeline container
const TimelineContainer = styled.div`
  position: relative;
`;

const TimelineLine = styled.div`
  position: absolute;
  left: 19px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

  @media (max-width: 768px) {
    display: none;
  }
`;

// Timeline item
const TimelineItem = styled.div`
  position: relative;
  padding-left: 52px;
  padding-bottom: 24px;

  &:last-child {
    padding-bottom: 0;
  }

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

const TimelineDot = styled.div`
  position: absolute;
  left: 12px;
  top: 4px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: ${({ color }) => color || '#fa4616'};
  border: 3px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#1F2937' : '#ffffff')};
  box-shadow: 0 0 0 2px
    ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

  @media (max-width: 768px) {
    display: none;
  }
`;

const NoteCard = styled.div`
  background: ${({ theme }) =>
    theme?.colors?.card?.background ||
    (theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(255, 255, 255, 0.95)')};
  border: 1px solid
    ${({ theme }) =>
      theme?.colors?.card?.border ||
      (theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0')};
  border-radius: 12px;
  padding: 16px 20px;
  transition: all 0.2s;

  &:hover {
    border-color: ${({ theme }) =>
      theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.2)' : '#cbd5e1'};
    box-shadow: ${({ theme }) =>
      theme?.name === 'dark'
        ? '0 4px 12px rgba(0, 0, 0, 0.3)'
        : '0 4px 12px rgba(0, 0, 0, 0.08)'};
  }
`;

const NoteHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

const NoteTitle = styled.h4`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`;

const NoteAttachment = styled.div`
  margin: 12px 0;

  a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    text-decoration: none;
  }

  img {
    max-width: 200px;
    max-height: 150px;
    border-radius: 8px;
    transition: all 0.2s;
    border: 1px solid
      ${({ theme }) =>
        theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#e2e8f0'};

    &:hover {
      transform: scale(1.02);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
  }
`;

const PdfAttachment = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(239, 68, 68, 0.1)'
      : 'rgba(239, 68, 68, 0.08)'};
  border: 1px solid
    ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.3)'
        : 'rgba(239, 68, 68, 0.2)'};
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.12)'};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15);
  }

  svg {
    flex-shrink: 0;
  }
`;

const PdfLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#FCA5A5' : '#DC2626')};
`;

const PdfIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
      stroke="#EF4444"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 2V8H20"
      stroke="#EF4444"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <text x="7" y="17" fontSize="6" fontWeight="bold" fill="#EF4444">
      PDF
    </text>
  </svg>
);

// Helper to check if URL is a PDF
const isPdfUrl = (url) => {
  if (!url) return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.endsWith('.pdf') ||
    lowerUrl.includes('.pdf?') ||
    lowerUrl.includes('/pdf')
  );
};

const NoteFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const NoteMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NoteDate = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoteAuthor = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textGray.muted};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NoteActions = styled.div`
  display: flex;
  gap: 8px;
`;

// Wrapper for action buttons
const ActionButtonWrapper = styled.div`
  button {
    width: 32px;
    height: 32px;
    padding: 0;
  }
`;

// Empty state
const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) =>
    theme.colors.primaryOpacity(theme.name === 'dark' ? 0.15 : 0.08)};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: ${({ theme }) => theme.colors.primary};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const EmptyStateTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 8px 0;
`;

const EmptyStateText = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.textGray.secondary};
  margin: 0 0 24px 0;
  max-width: 320px;
  margin-left: auto;
  margin-right: auto;
`;

// Read-only badge for account notes
const ReadOnlyBadge = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f1f5f9'};
  padding: 2px 8px;
  border-radius: 4px;
  margin-left: 8px;
`;

// Icons
const PlusIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const NotesIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const ClockIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const UserIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const EditIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const AccountIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const EventIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

// Helper to parse date value consistently (handles various formats)
const parseDate = (dateValue) => {
  if (!dateValue) return null;

  // Handle Unix timestamp (as string or number)
  if (typeof dateValue === 'string' && /^\d+$/.test(dateValue)) {
    // It's a numeric string (Unix timestamp in milliseconds)
    return new Date(parseInt(dateValue, 10));
  } else if (typeof dateValue === 'number') {
    return new Date(dateValue);
  } else {
    return new Date(dateValue);
  }
};

// Helper to format date
const formatDate = (dateValue) => {
  if (!dateValue) return 'Just now';

  const date = parseDate(dateValue);

  // Check if date is valid
  if (!date || isNaN(date.getTime())) return 'Just now';

  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
};

// Generate unique ID for new notes
let noteIdCounter = 0;
const generateNoteId = () => {
  noteIdCounter += 1;
  return `event-note-${Date.now()}-${noteIdCounter}`;
};

// State is lifted to parent (EditEventV2) so it persists across tab switches
const NotesTab = ({ event, loading, eventNotes = [], onEventNotesChange }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  // Fetch account data to get account notes
  const accountId = event?.account_id;
  const { data: accountData, loading: accountLoading } = useGetAccount(
    accountId ? parseInt(accountId) : null,
    !accountId
  );

  const accountNotes = accountData?.getAccount?.notes || [];

  // Combine all notes for filtering/counting
  const allNotes = useMemo(() => {
    const accountNotesWithSource = accountNotes.map((note) => ({
      ...note,
      source: 'account',
    }));
    const eventNotesWithSource = eventNotes.map((note) => ({
      ...note,
      source: 'event',
    }));
    return [...accountNotesWithSource, ...eventNotesWithSource];
  }, [accountNotes, eventNotes]);

  // Sort and filter notes (newest first)
  const filteredNotes = useMemo(() => {
    const sorted = [...allNotes].sort((a, b) => {
      const dateA = parseDate(a.created_at);
      const dateB = parseDate(b.created_at);
      // Handle invalid dates - put them at the end
      if (!dateA || isNaN(dateA.getTime())) return 1;
      if (!dateB || isNaN(dateB.getTime())) return -1;
      return dateB - dateA; // Newest first
    });

    if (activeFilter === 'all') return sorted;
    return sorted.filter((note) => note.type === activeFilter);
  }, [allNotes, activeFilter]);

  // Split into account and event notes for display
  const filteredAccountNotes = filteredNotes.filter(
    (note) => note.source === 'account'
  );
  const filteredEventNotes = filteredNotes.filter(
    (note) => note.source === 'event'
  );

  const handleAddNote = async (noteData, isEditing) => {
    if (isEditing) {
      // Update existing event note
      const updated = eventNotes.map((n) =>
        n.id === noteData.id
          ? {
              ...n,
              ...noteData,
              // Handle image removal when image_id is explicitly undefined/null
              ...(noteData.image_id === undefined && {
                image: null,
                image_id: null,
              }),
              updated_at: Date.now(),
            }
          : n
      );
      onEventNotesChange(updated);
      return true;
    }

    // Add new event note
    const newNote = {
      ...noteData,
      id: generateNoteId(),
      created_at: Date.now(),
      created_by: {
        id: 'current-user',
        formatted_name: 'You',
      },
      source: 'event',
    };

    onEventNotesChange([...eventNotes, newNote]);
    return true;
  };

  const handleEditClick = (note) => {
    setSelectedNote(note);
    setShowAddModal(true);
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!noteToDelete) return;

    onEventNotesChange(
      eventNotes.filter((note) => note.id !== noteToDelete.id)
    );
    setShowDeleteModal(false);
    setNoteToDelete(null);
  };

  // Get counts for each type
  const typeCounts = useMemo(() => {
    const counts = { all: allNotes.length };
    allNotes.forEach((note) => {
      counts[note.type] = (counts[note.type] || 0) + 1;
    });
    return counts;
  }, [allNotes]);

  const isLoading = loading || accountLoading;

  // Render note card
  const renderNoteCard = (note, isAccountNote = false) => {
    const typeConfig = NOTE_TYPES[note.type] || NOTE_TYPES.other;
    return (
      <TimelineItem key={note.id}>
        <TimelineDot color={typeConfig.color} />
        <NoteCard>
          <NoteHeader>
            <NoteTitle>{note.title}</NoteTitle>
            <Badge uppercase size="small">
              {typeConfig.label}
            </Badge>
          </NoteHeader>
          <CollapsibleText text={note.note} maxLength={300} />
          {note.image && (
            <NoteAttachment>
              {isPdfUrl(note.image) ? (
                <PdfAttachment
                  href={note.image}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <PdfIcon />
                  <PdfLabel>View PDF Attachment</PdfLabel>
                </PdfAttachment>
              ) : (
                <a href={note.image} target="_blank" rel="noopener noreferrer">
                  <img src={note.image} alt="Note attachment" />
                </a>
              )}
            </NoteAttachment>
          )}
          <NoteFooter>
            <NoteMeta>
              {note.created_by?.formatted_name && (
                <NoteAuthor>
                  <UserIcon />
                  {note.created_by.formatted_name}
                </NoteAuthor>
              )}
              <NoteDate>
                <ClockIcon />
                {formatDate(note.created_at)}
              </NoteDate>
            </NoteMeta>
            {!isAccountNote && (
              <NoteActions>
                <ActionButtonWrapper>
                  <ButtonV2
                    variant="icon"
                    type="button"
                    onClick={() => handleEditClick(note)}
                    title="Edit note"
                    icon={<EditIcon />}
                  />
                </ActionButtonWrapper>
                <ActionButtonWrapper>
                  <ButtonV2
                    danger
                    variant="icon"
                    type="button"
                    onClick={() => handleDeleteClick(note)}
                    title="Delete note"
                    icon={<TrashIcon />}
                  />
                </ActionButtonWrapper>
              </NoteActions>
            )}
          </NoteFooter>
        </NoteCard>
      </TimelineItem>
    );
  };

  return (
    <Container>
      <Card>
        <SectionHeader>
          <HeaderLeft>
            <SectionTitle>
              <NotesIcon />
              Event Notes
              {allNotes.length > 0 && (
                <ItemCount>({allNotes.length})</ItemCount>
              )}
            </SectionTitle>
            <SectionSubtitle>
              View account notes and add event-specific notes. Notes are only
              visible to administrators.
            </SectionSubtitle>
          </HeaderLeft>
          <AddButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={() => {
                setSelectedNote(null);
                setShowAddModal(true);
              }}
              icon={<PlusIcon />}
            >
              Add Event Note
            </ButtonV2>
          </AddButtonWrapper>
        </SectionHeader>

        {allNotes.length > 0 && (
          <FilterSection>
            <FilterChip
              active={activeFilter === 'all'}
              onClick={() => setActiveFilter('all')}
            >
              All ({typeCounts.all || 0})
            </FilterChip>
            {Object.entries(NOTE_TYPES).map(([key, { label, color }]) =>
              typeCounts[key] ? (
                <FilterChip
                  key={key}
                  active={activeFilter === key}
                  color={color}
                  onClick={() => setActiveFilter(key)}
                >
                  {label} ({typeCounts[key]})
                </FilterChip>
              ) : null
            )}
          </FilterSection>
        )}

        {isLoading ? (
          <EmptyState>
            <EmptyStateText>Loading notes...</EmptyStateText>
          </EmptyState>
        ) : allNotes.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <NotesIcon />
            </EmptyStateIcon>
            <EmptyStateTitle>No Notes Yet</EmptyStateTitle>
            <EmptyStateText>
              Add event-specific notes to keep track of important information
              about this event.
            </EmptyStateText>
            <AddButtonWrapper>
              <ButtonV2
                elevated
                type="button"
                onClick={() => {
                  setSelectedNote(null);
                  setShowAddModal(true);
                }}
                icon={<PlusIcon />}
              >
                Add Your First Note
              </ButtonV2>
            </AddButtonWrapper>
          </EmptyState>
        ) : (
          <>
            {/* Event Notes Section */}
            {filteredEventNotes.length > 0 && (
              <SubSection>
                <SubSectionHeader>
                  <SubSectionIcon color="#fa4616">
                    <EventIcon />
                  </SubSectionIcon>
                  <SubSectionTitle>Event Notes</SubSectionTitle>
                  <SubSectionCount>{filteredEventNotes.length}</SubSectionCount>
                </SubSectionHeader>
                <TimelineContainer>
                  <TimelineLine />
                  {filteredEventNotes.map((note) =>
                    renderNoteCard(note, false)
                  )}
                </TimelineContainer>
              </SubSection>
            )}

            {/* Account Notes Section */}
            {filteredAccountNotes.length > 0 && (
              <SubSection>
                <SubSectionHeader>
                  <SubSectionIcon color="#3b82f6">
                    <AccountIcon />
                  </SubSectionIcon>
                  <SubSectionTitle>
                    Account Notes
                    <ReadOnlyBadge>Read Only</ReadOnlyBadge>
                  </SubSectionTitle>
                  <SubSectionCount>
                    {filteredAccountNotes.length}
                  </SubSectionCount>
                </SubSectionHeader>
                <TimelineContainer>
                  <TimelineLine />
                  {filteredAccountNotes.map((note) =>
                    renderNoteCard(note, true)
                  )}
                </TimelineContainer>
              </SubSection>
            )}

            {/* Show message if filter yields no results but notes exist */}
            {filteredNotes.length === 0 && allNotes.length > 0 && (
              <EmptyState>
                <EmptyStateTitle>No Notes Found</EmptyStateTitle>
                <EmptyStateText>
                  No notes found for the "
                  {NOTE_TYPES[activeFilter]?.label || activeFilter}" category.
                </EmptyStateText>
              </EmptyState>
            )}
          </>
        )}
      </Card>

      <AddNoteModal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setSelectedNote(null);
        }}
        onSubmit={handleAddNote}
        note={selectedNote}
        defaultType="event"
        modalTitle="Add Event Note"
      />

      <DeleteConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setNoteToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        itemName={noteToDelete?.title || ''}
        itemType="note"
      />
    </Container>
  );
};

NotesTab.propTypes = {
  event: PropTypes.object,
  loading: PropTypes.bool,
  onEventNotesChange: PropTypes.func,
};

export default NotesTab;
